import TokenizedText from './TokenizedText';
import TranslationNode from './TranslationNode';
import TranslationCache from './TranslationCache';

class TranslationLinkNode extends TranslationNode {
    /**
     * Generate a new TranslationLinkNode
     *
     * @param {HTMLAnchorElement} element HTML link element
     * @param {Element} _ parent node
     */
    constructor(element, _) {
        super(element, element);

        this.tagWrapper = 'a';
    }

    shouldWeTranslate(text) {
        const trimmed = text.trim();

        // ensure proper link that we support
        const allowedProtocol = ['http:', 'https:', 'mailto:', 'tel:', 'ftp:'];
        try {
            const url = new URL(trimmed, window.location.href);

            if (!allowedProtocol.includes(url.protocol)) {
                return false;
            }

            const currentUrl = new URL(window.location.href);

            // check if the only thing changed is the hash
            if (
                currentUrl.protocol === url.protocol &&
                currentUrl.origin === url.origin &&
                currentUrl.pathname === url.pathname &&
                currentUrl.search === url.search &&
                currentUrl.hash !== url.hash
            ) {
                return false;
            }

            return true;
        } catch (e) {
            return false;
        }

        // call parent
        return super.shouldWeTranslate(text);
    }

    /**
     * Convert/tokenize the element to be translated.
     * 
     * @param {boolean} [ignoreCached=false] Ignore cached tokenized, this will also not save the tokenized to cache
     * @returns {TokenizedText[]} Collection of text to be translated
     */
    tokenize(ignoreCached = false) {
        if (this._cachedTokenized !== undefined && !ignoreCached) {
            return this._cachedTokenized;
        }

        const hrefText = this.element.getAttribute('href');

        const mergedNode = [];
        if (hrefText && this.shouldWeTranslate(hrefText)) {
            const altNode = new TokenizedText(this, 0, '', {href: hrefText});
            altNode.subId = 'href';
            mergedNode.push(altNode);
        }

        if (!mergedNode.length) {
            throw new Error('No attribute to translate in link element');
        }

        if (!ignoreCached) {
            this._cachedTokenized = mergedNode;
        }

        return mergedNode;
    }

    /**
     * Replace the text for this element node with the new text
     *
     * @param {TokenizedText} newToken The replacement token
     * @param {string} lang language code
     * @param {import('./TranslationNode').TokenApplyOptions} [options] Options when applying new token
     */
    applyChange(newToken, lang, options) {
        const mergedOptions = Object.assign({}, {
            dryRun: false,
            skipCache: false,
        }, options || {});

        if (!mergedOptions.skipCache) {
            this.cachedIndex.push(newToken.index);
        }

        const subId = newToken.subId;

        if (!subId) {
            // This should not happen
            return;
        }

        if (!newToken.metaExtra) {
            // This should not happen
            return;
        }

        let metadataText = newToken.metaExtra.href;

        this.applyAttributeChange('href', metadataText, {
            ...mergedOptions,
            language: lang,
            subId,
        });
        this.attachEditor(newToken);
    }

    /**
     * Noop, we don't need to blur the parent element for link
     */
    blurParent() {}

    /**
     * Noop, we don't need to unblur the parent element for link
     */
    unblurParent() {}
}

export default TranslationLinkNode;
